import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { ingestorsApi } from './ingestors/api';
import type { EventType } from './ingestors/types';

// Admin registry of event types. Scoring rules can only reference a
// registered type (FK enforced at the DB). This page is the only way
// to introduce a new event type — you can't free-type one in the
// scoring rules form. See ADR-0010.
export function EventTypesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['event-types'],
    queryFn: () => ingestorsApi.listEventTypes(),
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ id: '', description: '', source_ingestor_type: '' });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => ingestorsApi.createEventType({
      id: form.id,
      description: form.description,
      source_ingestor_type: form.source_ingestor_type || null,
    }),
    onSuccess: () => {
      setCreating(false);
      setForm({ id: '', description: '', source_ingestor_type: '' });
      qc.invalidateQueries({ queryKey: ['event-types'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => ingestorsApi.deleteEventType(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-types'] }),
    onError: (e) => {
      setErr(e instanceof ApiError && e.body && typeof e.body === 'object' && 'error' in e.body
        ? (e.body as { error: string }).error === 'event_type_in_use'
          ? 'Cannot delete: a scoring rule still uses this event type.'
          : 'Delete failed.'
        : 'Delete failed.');
    },
  });

  const save = async () => {
    setErr(null);
    if (!form.id.trim()) { setErr('Event type id is required.'); return; }
    try {
      await create.mutateAsync();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Create failed.');
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Event types</h1>
          <p className="text-ink-muted">
            The registry of event types that scoring rules can reference. A rule's
            event type must be registered here — you can't free-type one in the
            scoring rules form. Add a new type here first, then write rules for it.
          </p>
        </div>
        <button className="btn-primary" onClick={() => { setCreating((v) => !v); setErr(null); }} disabled={creating}>
          + Add event type
        </button>
      </header>

      {error ? <p className="text-sm text-accent-danger">Failed to load event types.</p> : null}
      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}

      {creating ? (
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">New event type</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="label">Event type id</label>
              <input
                className="input font-mono"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                placeholder="pr.opened"
              />
              <p className="text-xs text-ink-dim">Lowercase, dots/hyphens/underscores. This is the canonical string ingestors emit.</p>
            </div>
            <div className="space-y-1">
              <label className="label">Source ingestor</label>
              <input
                className="input"
                value={form.source_ingestor_type}
                onChange={(e) => setForm({ ...form, source_ingestor_type: e.target.value })}
                placeholder="github / jira / manual"
              />
              <p className="text-xs text-ink-dim">Optional. Which ingestor emits this event.</p>
            </div>
            <div className="space-y-1">
              <label className="label">Description</label>
              <input
                className="input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="GitHub PR opened"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn" onClick={() => { setCreating(false); setErr(null); }}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={create.isPending || !form.id.trim()}>
              {create.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Event type</th>
              <th>Source</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.event_types ?? []).map((t: EventType) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="py-2 font-mono text-xs">{t.id}</td>
                <td className="text-ink-muted">{t.source_ingestor_type ?? '—'}</td>
                <td className="text-ink-muted">{t.description || '—'}</td>
                <td className="text-right">
                  <button
                    className="btn-danger"
                    onClick={() => { setErr(null); remove.mutate(t.id); }}
                    title="Delete (blocked if a scoring rule uses it)"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && !(data?.event_types ?? []).length ? (
              <tr>
                <td colSpan={4} className="py-4 text-center text-ink-muted">No event types registered.</td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={4} className="py-4 text-center text-ink-muted">Loading…</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}